import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Btn, Input, IOSwitch, Spinner } from "../components/ui";
import { Logo } from "../components/app/Logo";
import { codeAnfordern, codeEinloesen, holeAuthConfig } from "../api/client";
import { ApiError } from "../lib/fetchJson";
import { codeRuecktaste, codeSetzen, codeText, codeVollstaendig, leererCode, CODE_LAENGE } from "../lib/code";
import { BEHAELTER_ID, ladeTurnstile } from "../lib/turnstile";
import { TX } from "../lib/texte";
import { sekundenKurz } from "../lib/format";
import { useTitel } from "../hooks/useTitel";
import { useAuthStore } from "../store/useAuthStore";

// ============================================================================
// Die Anmeldung. Portiert aus /opt/fragenbuero/frontend/src/screens/
// Anmelden.tsx (§5.7.1), OHNE Schritt 3: der Anzeigename wird beim ersten
// Anmelden serverseitig aus dem Ortsteil der Adresse vorbelegt, ohne
// Eindeutigkeitszwang (T26/A13) — es gibt also keinen Spitznamen-Apparat,
// kein `nicknameFrei`, keinen 428-Zweig und keinen „frei"-Punkt.
//
// VIER FALLEN, alle schon einmal zugeschlagen, alle hier gelöst — nicht
// wegräumen (§5.7.1):
//
// (a) Der Doppelabsende-Riegel liegt in einem REF, nicht im Zustand: das
//     Autofill des Telefons löst `submit` UND `click` im selben Tick aus, und
//     ein Zustandswert wäre beim zweiten Aufruf noch der alte.
// (b) Der Turnstile-Effekt darf bei `schritt === "code"` NICHT aussteigen.
//     Sonst räumt sein Cleanup das Widget ab, und „Code erneut senden"
//     schickt für immer dieselbe, verbrauchte Marke los (Cloudflare:
//     timeout-or-duplicate) — was serverseitig jedes Mal eine captcha_fail-
//     Zeile gegen das IP-Kontingent des ganzen Büros schreibt.
// (c) `pruefungZurueck()` läuft nach JEDEM Absenden, auch nach Erfolg: eine
//     Wertmarke gilt genau einmal.
// (d) Nur das ERSTE Codefeld trägt `autoComplete="one-time-code"` — iOS füllt
//     dort den ganzen Code ein, `codeSetzen()` verteilt ihn weiter.
//
// Der Behälter heißt `pruefung`, NIEMALS `turnstile`: der Browser legt für
// jedes Element mit id eine gleichnamige Eigenschaft auf `window` an, und
// `window.turnstile` wäre dann das <div> — die api.js hielte sich für schon
// geladen und zeichnete nichts.
// ============================================================================

type Schritt = "email" | "code";

const ERNEUT_SPERRE_S = 60;

export function Anmelden() {
  useTitel(TX.anmelden.titel);
  const navigate = useNavigate();
  // Wohin nach der Anmeldung (T48: die Freigabeseite für Claude). Nur eigene
  // Pfade — nie eine fremde Adresse aus dem Verlaufszustand.
  const zustand = useLocation().state as { zurueck?: unknown } | null;
  const zurueck =
    typeof zustand?.zurueck === "string" && /^\/(?!\/)/.test(zustand.zurueck) && !zustand.zurueck.startsWith("/anmelden")
      ? zustand.zurueck
      : "/";
  const status = useAuthStore((s) => s.auth.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  const laden = useAuthStore((s) => s.laden);

  const [schritt, setSchritt] = useState<Schritt>("email");
  const [email, setEmail] = useState("");
  // Vorbelegt AN (T26): man zeichnet stundenlang an einem Plan.
  const [bleiben, setBleiben] = useState(true);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState<string[]>(leererCode);
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [wackelt, setWackelt] = useState(false);
  const [erneutAb, setErneutAb] = useState(0);
  const [jetzt, setJetzt] = useState(() => Date.now());

  const [sitekey, setSitekey] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<string | undefined>(undefined);

  // (a) Der Doppelabsende-Riegel. Ref, nicht Zustand.
  const laeuftRef = useRef(false);
  const feldRefs = useRef<(HTMLInputElement | null)[]>([]);

  /* --- Turnstile -------------------------------------------------------- */

  useEffect(() => {
    let lebt = true;
    holeAuthConfig()
      .then((c) => {
        if (!lebt) return;
        setSitekey(c.turnstile_sitekey);
        setDomains(c.allowed_domains ?? []);
      })
      .catch(() => {
        // Ohne Konfiguration bleibt der Knopf frei; scheitert die Prüfung
        // serverseitig, kommt der Grund als deutscher Satz zurück.
        if (lebt) setSitekey(null);
      });
    return () => {
      lebt = false;
    };
  }, []);

  // (b) Schritt 1 UND 2 brauchen ein lebendes Widget.
  useEffect(() => {
    if (!sitekey) return;
    let lebt = true;
    let id: string | undefined;
    ladeTurnstile()
      .then((api) => {
        if (!lebt || !document.getElementById(BEHAELTER_ID)) return;
        // Explizites render(): mit render=explicit zeichnet Cloudflare von
        // sich aus nichts mehr, und der Sitekey kommt ohnehin erst zur
        // Laufzeit aus GET /auth/config.
        id = api.render(`#${BEHAELTER_ID}`, {
          sitekey,
          action: "login",
          theme: "auto",
          callback: (t) => setToken(t),
          "error-callback": () => {
            setToken(null);
            setFehler(TX.anmelden.pruefungFehler);
          },
          "expired-callback": () => setToken(null),
        });
        widgetRef.current = id;
      })
      .catch((e: unknown) => {
        if (lebt) setFehler(e instanceof Error ? e.message : TX.anmelden.pruefungFehler);
      });
    return () => {
      lebt = false;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* schon weg */
        }
      }
      widgetRef.current = undefined;
    };
  }, [sitekey, schritt]);

  /** (c) Nach JEDEM Absenden: die Wertmarke ist verbraucht. */
  const pruefungZurueck = useCallback(() => {
    setToken(null);
    if (widgetRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetRef.current);
      } catch {
        /* Widget ist weg — dann gibt es auch nichts zurückzusetzen */
      }
    }
  }, []);

  /* --- Countdown „Code erneut senden" ----------------------------------- */

  useEffect(() => {
    if (!erneutAb) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 500);
    return () => clearInterval(uhr);
  }, [erneutAb]);
  const restS = erneutAb ? Math.max(0, (erneutAb - jetzt) / 1000) : 0;

  /* --- Schritt 1: Code anfordern ---------------------------------------- */

  const codeSchicken = async () => {
    if (laeuftRef.current) return;
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler("");
    try {
      const a = await codeAnfordern(email.trim(), token);
      setChallenge(a.challenge);
      setCode(leererCode());
      setSchritt("code");
      setErneutAb(Date.now() + ERNEUT_SPERRE_S * 1000);
      setJetzt(Date.now());
      pruefungZurueck();
      setTimeout(() => feldRefs.current[0]?.focus(), 60);
    } catch (e) {
      setFehler(e instanceof ApiError ? e.message : TX.anmelden.startFehler);
      pruefungZurueck();
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  };

  const erneutSchicken = async () => {
    if (restS > 0 || laeuftRef.current) return;
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler("");
    try {
      const a = await codeAnfordern(email.trim(), token);
      setChallenge(a.challenge);
      setCode(leererCode());
      setErneutAb(Date.now() + ERNEUT_SPERRE_S * 1000);
      setJetzt(Date.now());
      pruefungZurueck();
      feldRefs.current[0]?.focus();
    } catch (e) {
      setFehler(e instanceof ApiError ? e.message : TX.anmelden.startFehler);
      pruefungZurueck();
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  };

  /* --- Schritt 2: Code einlösen ----------------------------------------- */

  const absendenCode = useCallback(
    async (ziffern: string[]) => {
      // Ein Code ist EINMAL gültig. Ohne diesen Riegel schickt das Autofill
      // des Telefons ihn zweimal, der zweite Versuch läuft ins Leere und die
      // Person liest „Der Code stimmt nicht.", obwohl alles stimmte.
      if (laeuftRef.current) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        await codeEinloesen(challenge, codeText(ziffern), bleiben);
        // Es gibt keinen dritten Schritt: angemeldet, /me holen, weiter (§5.7.1).
        angemeldet();
        await laden();
        navigate(zurueck, { replace: true });
      } catch (e) {
        setFehler(e instanceof ApiError ? e.message : TX.anmelden.codeFehler);
        setCode(leererCode());
        setWackelt(true);
        setTimeout(() => setWackelt(false), 320);
        setTimeout(() => feldRefs.current[0]?.focus(), 40);
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [challenge, bleiben, angemeldet, laden, navigate, zurueck],
  );

  const ziffernSetzen = (index: number, roh: string) => {
    // Tippt jemand in ein schon gefülltes Kästchen, steht dort kurz "12".
    // Gemeint ist die NEUE Ziffer, nicht ein zweistelliger Einschub — sonst
    // rutscht die alte Ziffer nach rechts weiter.
    const alt = code[index] ?? "";
    const eingabe = alt && roh.length === 2 && roh.startsWith(alt) ? roh.slice(1) : roh;
    const { code: neu, fokus } = codeSetzen(code, index, eingabe);
    setCode(neu);
    feldRefs.current[fokus]?.focus();
    // Die sechste Ziffer schickt die Karte selbst ab.
    if (codeVollstaendig(neu)) void absendenCode(neu);
  };

  // Wer schon angemeldet ist, hat hier nichts verloren.
  useEffect(() => {
    if (status === "angemeldet") navigate(zurueck, { replace: true });
  }, [status, navigate, zurueck]);

  // Der Rückfall ist DERSELBE Satzbauer mit den beiden Hausdomänen (§5.7.1
  // Punkt 5), nicht ein zweiter, fest getippter Satz: eine deutsche
  // Zeichenkette in einer Komponente wäre ein Bruch von §9.6.3 — und der
  // zweite Wortlaut wäre der, der beim nächsten Mal vergessen wird.
  const domainHinweis = TX.anmelden.domains(domains.length ? [...domains] : ["lilasp.de", "lilasp.gmbh"]);

  return (
    <main className="huelle" style={{ maxWidth: 460, paddingTop: 48 }}>
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        <Logo />
      </div>

      <div className="card karte-ein" style={{ padding: 26 }}>
        {schritt === "email" && (
          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              void codeSchicken();
            }}
          >
            <h1 className="seitentitel" style={{ fontSize: 22, marginBottom: 6 }}>
              {TX.anmelden.titel}
            </h1>
            <p className="unterzeile" style={{ marginBottom: 20 }}>
              {TX.anmelden.unterzeile}
            </p>

            <label className="lbl" htmlFor="zp-email">
              {TX.anmelden.email}
            </label>
            <Input
              id="zp-email"
              type="email"
              value={email}
              autoFocus
              required
              autoComplete="email"
              inputMode="email"
              placeholder={TX.anmelden.emailPlatzhalter}
              onChange={(ev) => setEmail(ev.target.value)}
            />
            <p className="hinweiszeile" style={{ marginTop: 6 }}>
              {domainHinweis}
            </p>

            <div className="reihe" style={{ justifyContent: "space-between", margin: "16px 0", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{TX.anmelden.bleiben}</span>
              <IOSwitch on={bleiben} onChange={setBleiben} />
            </div>

            {/* (Behälter) pruefung, NIE turnstile. Ohne Sitekey bleibt er ohne
                Höhe und der Knopf ist frei. */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0 }} />

            <p className="fehlerzeile" role="status">
              {fehler}
            </p>

            <Btn
              type="submit"
              size="lg"
              style={{ width: "100%", justifyContent: "center" }}
              disabled={laeuft || !email.trim() || (!!sitekey && !token)}
            >
              {laeuft && <Spinner size={14} color="currentColor" />}
              {TX.anmelden.codeSchicken}
            </Btn>
          </form>
        )}

        {schritt === "code" && (
          <form
            className={wackelt ? "wackel" : ""}
            onSubmit={(ev) => {
              ev.preventDefault();
              if (codeVollstaendig(code)) void absendenCode(code);
            }}
          >
            <h1 className="seitentitel" style={{ fontSize: 22, marginBottom: 6 }}>
              {TX.anmelden.codeTitel}
            </h1>
            <p className="unterzeile" style={{ marginBottom: 20 }}>
              {TX.anmelden.codeUnterzeile}
            </p>

            <div className="code-reihe">
              {code.map((z, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    feldRefs.current[i] = el;
                  }}
                  className={`codebox${codeVollstaendig(code) ? " puls" : ""}`}
                  value={z}
                  inputMode="numeric"
                  // (d) Nur das erste Feld trägt one-time-code.
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  autoFocus={i === 0}
                  maxLength={CODE_LAENGE}
                  aria-label={TX.anmelden.ziffer(i + 1, CODE_LAENGE)}
                  disabled={laeuft}
                  onChange={(ev) => ziffernSetzen(i, ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Backspace") {
                      ev.preventDefault();
                      const { code: neu, fokus } = codeRuecktaste(code, i);
                      setCode(neu);
                      feldRefs.current[fokus]?.focus();
                    }
                    if (ev.key === "ArrowLeft") feldRefs.current[Math.max(0, i - 1)]?.focus();
                    if (ev.key === "ArrowRight") feldRefs.current[Math.min(CODE_LAENGE - 1, i + 1)]?.focus();
                  }}
                  onPaste={(ev) => {
                    ev.preventDefault();
                    ziffernSetzen(i, ev.clipboardData.getData("text"));
                  }}
                />
              ))}
            </div>

            {/* Dasselbe <div> wie in Schritt 1 — immer nur EINES im Baum, sonst
                gäbe es die Kennung zweimal. Der Effekt oben zeichnet hierher ein
                frisches Widget, damit „Code erneut senden" eine unverbrauchte
                Marke hat (Falle b). */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0, marginTop: 14 }} />

            <p className="fehlerzeile" role="status" style={{ textAlign: "center" }}>
              {fehler}
            </p>

            <div className="reihe" style={{ justifyContent: "space-between", marginTop: 8 }}>
              <Btn variant="ghost" size="sm" type="button" onClick={() => setSchritt("email")}>
                {TX.anmelden.andereAdresse}
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                type="button"
                disabled={restS > 0 || laeuft || (!!sitekey && !token)}
                onClick={() => void erneutSchicken()}
              >
                {restS > 0 ? TX.anmelden.erneutIn(sekundenKurz(restS)) : TX.anmelden.erneut}
              </Btn>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
